import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapPin, ChevronDown, X, Search, Building2, Map, Home } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LocationSelector = ({ value, onChange, mode = "cascade" }) => {
  const [judete, setJudete] = useState([]);
  const [localitati, setLocalitati] = useState([]);
  const [sectoare, setSectoare] = useState([]);
  const [zone, setZone] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeout = useRef(null);

  // Selected values
  const [selectedJudet, setSelectedJudet] = useState(value?.judet || "");
  const [selectedLocalitate, setSelectedLocalitate] = useState(value?.localitate || "");
  const [selectedSector, setSelectedSector] = useState(value?.sector || "");
  const [selectedZona, setSelectedZona] = useState(value?.zona || "");
  const [selectedStrada, setSelectedStrada] = useState(value?.strada || "");

  // Load județe on mount
  useEffect(() => {
    fetchJudete();
  }, []);

  // Load localități when județ changes
  useEffect(() => {
    if (selectedJudet) {
      fetchLocalitati(selectedJudet);
    } else {
      setLocalitati([]);
      setSectoare([]);
      setZone([]);
    }
  }, [selectedJudet]);

  // Update parent when selection changes
  useEffect(() => {
    onChange?.({
      judet: selectedJudet,
      localitate: selectedLocalitate,
      sector: selectedSector,
      zona: selectedZona,
      strada: selectedStrada
    });
  }, [selectedJudet, selectedLocalitate, selectedSector, selectedZona, selectedStrada]);

  const fetchJudete = async () => {
    try {
      const response = await axios.get(`${API}/geo/judete`);
      setJudete(response.data);
    } catch (error) {
      console.error("Error fetching judete:", error);
    }
  };

  const fetchLocalitati = async (codJudet) => {
    try {
      const response = await axios.get(`${API}/geo/localitati/${codJudet}`);
      setLocalitati(response.data.localitati || []);
      setSectoare(response.data.sectoare || []);
      setZone(response.data.zone || []);
    } catch (error) {
      console.error("Error fetching localitati:", error);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await axios.get(`${API}/geo/search?q=${encodeURIComponent(query)}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error("Error searching:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleSelectSearchResult = (result) => {
    switch (result.type) {
      case "judet":
        setSelectedJudet(result.cod);
        setSelectedLocalitate("");
        setSelectedSector("");
        setSelectedZona("");
        break;
      case "localitate":
        setSelectedJudet(result.cod_judet);
        setSelectedLocalitate(result.nume);
        setSelectedSector("");
        setSelectedZona("");
        break;
      case "sector":
        setSelectedJudet(result.cod_judet);
        setSelectedLocalitate("");
        setSelectedSector(result.nume);
        setSelectedZona("");
        break;
      case "zona":
        setSelectedJudet(result.cod_judet);
        if (result.parent?.includes("Sector")) {
          setSelectedSector(result.parent);
          setSelectedLocalitate("");
        } else {
          setSelectedLocalitate(result.parent);
          setSelectedSector("");
        }
        setSelectedZona(result.nume);
        break;
    }
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const clearSelection = () => {
    setSelectedJudet("");
    setSelectedLocalitate("");
    setSelectedSector("");
    setSelectedZona("");
    setSelectedStrada("");
  };

  const getDisplayText = () => {
    const parts = [];
    if (selectedZona) parts.push(selectedZona);
    if (selectedSector) parts.push(selectedSector);
    if (selectedLocalitate) parts.push(selectedLocalitate);
    if (selectedJudet) {
      const judet = judete.find(j => j.cod === selectedJudet);
      if (judet) parts.push(judet.nume);
    }
    return parts.join(", ") || "Selectează locația";
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "judet": return <Map className="w-4 h-4 text-blue-500" />;
      case "localitate": return <Building2 className="w-4 h-4 text-emerald-500" />;
      case "sector": return <Building2 className="w-4 h-4 text-amber-500" />;
      case "zona": return <Home className="w-4 h-4 text-purple-500" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  const isBucuresti = selectedJudet === "B";

  return (
    <div className="space-y-3">
      {/* Quick Search */}
      <Popover open={showSearch} onOpenChange={setShowSearch}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              data-testid="location-search-input"
              placeholder="Caută oraș, zonă, cartier..."
              value={searchQuery}
              onChange={(e) => {
                handleSearch(e.target.value);
                setShowSearch(true);
              }}
              onClick={() => setShowSearch(true)}
              className="pl-10 h-12 rounded-xl"
            />
            {(selectedJudet || searchQuery) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded-full"
              >
                <X className="w-4 h-4 text-stone-400" />
              </button>
            )}
          </div>
        </PopoverTrigger>
        
        {searchResults.length > 0 && (
          <PopoverContent className="w-[400px] p-0" align="start">
            <ScrollArea className="h-[300px]">
              <div className="p-2">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectSearchResult(result)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-left transition-colors"
                  >
                    {getTypeIcon(result.type)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{result.nume}</p>
                      <p className="text-sm text-stone-500 truncate">{result.display}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {result.type === "judet" ? "Județ" : 
                       result.type === "localitate" ? result.tip || "Localitate" :
                       result.type === "sector" ? "Sector" : "Zonă"}
                    </Badge>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        )}
      </Popover>

      {/* Cascade Selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Județ */}
        <Select value={selectedJudet} onValueChange={(v) => {
          setSelectedJudet(v);
          setSelectedLocalitate("");
          setSelectedSector("");
          setSelectedZona("");
        }}>
          <SelectTrigger data-testid="judet-select" className="h-11 rounded-xl">
            <SelectValue placeholder="Județ" />
          </SelectTrigger>
          <SelectContent>
            <ScrollArea className="h-[300px]">
              {judete.map((judet) => (
                <SelectItem key={judet.cod} value={judet.cod}>
                  {judet.nume}
                </SelectItem>
              ))}
            </ScrollArea>
          </SelectContent>
        </Select>

        {/* Localitate / Sector (pentru București) */}
        {isBucuresti ? (
          <Select value={selectedSector} onValueChange={(v) => {
            setSelectedSector(v);
            setSelectedZona("");
          }}>
            <SelectTrigger data-testid="sector-select" className="h-11 rounded-xl">
              <SelectValue placeholder="Sector" />
            </SelectTrigger>
            <SelectContent>
              {sectoare.map((sector) => (
                <SelectItem key={sector.nume} value={sector.nume}>
                  {sector.nume}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Select 
            value={selectedLocalitate} 
            onValueChange={(v) => {
              setSelectedLocalitate(v);
              setSelectedZona("");
            }}
            disabled={!selectedJudet}
          >
            <SelectTrigger data-testid="localitate-select" className="h-11 rounded-xl">
              <SelectValue placeholder="Oraș / Comună" />
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[300px]">
                {localitati.map((loc) => (
                  <SelectItem key={loc.nume} value={loc.nume}>
                    {loc.nume} {loc.tip && <span className="text-stone-400">({loc.tip})</span>}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        )}

        {/* Zonă / Cartier */}
        <Select 
          value={selectedZona} 
          onValueChange={setSelectedZona}
          disabled={!selectedJudet || (!selectedLocalitate && !selectedSector)}
        >
          <SelectTrigger data-testid="zona-select" className="h-11 rounded-xl">
            <SelectValue placeholder="Zonă / Cartier" />
          </SelectTrigger>
          <SelectContent>
            <ScrollArea className="h-[300px]">
              {zone
                .filter(z => {
                  if (isBucuresti && selectedSector) {
                    return z.sector === selectedSector;
                  }
                  if (selectedLocalitate) {
                    return z.localitate === selectedLocalitate;
                  }
                  return true;
                })
                .map((zona) => (
                  <SelectItem key={zona.nume} value={zona.nume}>
                    {zona.nume}
                  </SelectItem>
                ))}
            </ScrollArea>
          </SelectContent>
        </Select>

        {/* Stradă (text liber) */}
        <Input
          data-testid="strada-input"
          placeholder="Stradă (opțional)"
          value={selectedStrada}
          onChange={(e) => setSelectedStrada(e.target.value)}
          className="h-11 rounded-xl"
        />
      </div>

      {/* Selected Location Display */}
      {selectedJudet && (
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="w-4 h-4 text-emerald-600" />
          <span className="text-sm text-stone-600">{getDisplayText()}</span>
          <button
            onClick={clearSelection}
            className="text-stone-400 hover:text-stone-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
